import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ShopProduct } from '@/hooks/useShopProducts';
import { getShopProductRegularPrice, getShopProductSalePrice } from '@/lib/shopPricing';

const SHOP_CART_STORAGE_KEY = 'shop-cart-v1';

export interface ShopCartItem {
  product: ShopProduct;
  quantity: number;
}

interface ShopCartState {
  items: ShopCartItem[];
  couponCode: string | null;
  couponDiscount: number;
  couponDiscountPercent: number;
  addItem: (product: ShopProduct) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  updateItemPrice: (productId: string, newPrice: number) => void;
  getItemQuantity: (productId: string) => number;
  getTotalItems: () => number;
  /** Get total using REGULAR prices (for guests / default display) */
  getTotalPrice: () => number;
  /** Get total using SALE prices (for verified club members only) */
  getMemberTotalPrice: () => number;
  setCoupon: (code: string, discount: number, percent: number) => void;
  clearCoupon: () => void;
  clearCart: () => void;
  clearCartAndStorage: () => void;
}

export const useShopCartStore = create<ShopCartState>()(
  persist(
    (set, get) => ({
      items: [],
      couponCode: null,
      couponDiscount: 0,
      couponDiscountPercent: 0,

      addItem: (product) => {
        const state = get();
        const existing = state.items.find(i => i.product.id === product.id);

        if (existing) {
          set({
            items: state.items.map(i =>
              i.product.id === product.id
                ? { ...i, quantity: i.quantity + 1 }
                : i
            ),
          });
        } else {
          set({
            items: [...state.items, { product, quantity: 1 }],
          });
        }
      },

      removeItem: (productId) => {
        set({
          items: get().items.filter(i => i.product.id !== productId),
        });
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }
        set({
          items: get().items.map(i =>
            i.product.id === productId
              ? { ...i, quantity }
              : i
          ),
        });
      },

      updateItemPrice: (productId, newPrice) => {
        set({
          items: get().items.map(i =>
            i.product.id === productId
              ? { ...i, product: { ...i.product, price: newPrice } }
              : i
          ),
        });
      },

      getItemQuantity: (productId) => {
        const item = get().items.find(i => i.product.id === productId);
        return item?.quantity || 0;
      },

      getTotalItems: () => {
        return get().items.reduce((sum, i) => sum + i.quantity, 0);
      },

      getTotalPrice: () => {
        // Guest Price = Selling Price (product.price)
        // Previously used Regular Price (compareAt), which was incorrect for items on sale to general public
        return get().items.reduce(
          (sum, i) => sum + i.product.price * i.quantity,
          0
        );
      },

      getMemberTotalPrice: () => {
        // Member Price = Selling Price - 10% Discount
        const total = get().items.reduce(
          (sum, i) => sum + i.product.price * i.quantity,
          0
        );
        return Math.round(total * 0.9);
      },

      setCoupon: (code, discount, percent) =>
        set({ couponCode: code, couponDiscount: discount, couponDiscountPercent: percent }),

      clearCoupon: () =>
        set({ couponCode: null, couponDiscount: 0, couponDiscountPercent: 0 }),

      clearCart: () => set({ items: [], couponCode: null, couponDiscount: 0, couponDiscountPercent: 0 }),

      // Clear cart AND remove from localStorage to prevent old orders from returning
      clearCartAndStorage: () => {
        set({ items: [], couponCode: null, couponDiscount: 0, couponDiscountPercent: 0 });
        try {
          localStorage.removeItem(SHOP_CART_STORAGE_KEY);
        } catch (e) {
          console.error('Failed to clear cart storage:', e);
        }
      },
    }),
    {
      name: SHOP_CART_STORAGE_KEY,
      partialize: (state) => ({
        items: state.items,
        // Do NOT persist coupon state to avoid stale coupons
      }),
    }
  )
);
